import { useEffect, useState, type ReactNode } from 'react';
// Check / X / Plus stay on lucide: they are UI affordances (a tick in a compare
// table, an accordion toggle), not concept icons.
// Everything descriptive comes from the abstract set.
import { Check, Plus, X } from 'lucide-react';
import {
  AbAegis, AbApex, AbAscend, AbBalance, AbBoard, AbBrackets, AbBroadcast, AbCase, AbCommerce,
  AbCredential, AbCycle, AbDialogue, AbDoc, AbGlobeMesh, AbGrid, AbImprint, AbJoin, AbLens,
  AbPerson, AbPing, AbPurse, AbSeal, AbSpectrum, AbStack, AbStroke, AbStructure, AbTuner,
  type AbIcon,
} from './AbstractIcons';
// The audience grid tiles at 56px, so those marks come from the dimensional
// set. Everything else in this file is a small inline badge and stays flat.
import {
  AbApex as D_Apex, AbBalance as D_Balance, AbBoard as D_Board, AbBrackets as D_Brackets,
  AbBroadcast as D_Broadcast, AbCommerce as D_Commerce, AbCredential as D_Credential,
  AbCycle as D_Cycle, AbGlobeMesh as D_GlobeMesh, AbImprint as D_Imprint, AbLens as D_Lens,
  AbPurse as D_Purse, AbSeal as D_Seal, AbSpectrum as D_Spectrum, AbStroke as D_Stroke,
  AbStructure as D_Structure,
} from './Icons3D';
import '@/styles/landingHT.css';

/* ────────────────────────────────────────────────────────────────────────────
   Building blocks for the human-touch landings (`.ht`, see styles/landingHT.css)

     /            RunComply.tsx        — setup + accounting + legal
     /accounting  Accounting.tsx       — books, VAT, corporate tax
     /corporate   CorporateServices.tsx — setup, visas, PRO   (orange variant)

   The three reference prototypes share these sections almost verbatim, so they
   live here once. Anything genuinely page-specific (hero visuals, the pillars
   grid, pricing) stays in the page.
   ──────────────────────────────────────────────────────────────────────────── */

/** Scroll-reveal for `.rv` elements inside `.ht`. One observer for the page. */
export function HTReveal() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const els = Array.from(document.querySelectorAll<HTMLElement>('.ht .rv'));
    if (!els.length) return;
    const io = new IntersectionObserver(entries => {
      for (const e of entries) {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }
    }, { rootMargin: '0px 0px -6% 0px', threshold: .08 });
    for (const el of els) {
      el.setAttribute('data-reveal', '');
      // Already on screen at mount → show at once, so the fold is never blank.
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) el.classList.add('in');
      else io.observe(el);
    }
    return () => io.disconnect();
  }, []);
  return null;
}

/** The hero photo, cycling. The frames are stacked in the same `.photo` frame
    and cross-faded, so the slot never reflows and the floating cards over it
    never move. One frame in, and it behaves exactly like the plain `<img>` this
    replaced.

    Three things it deliberately does not do: run while the tab is hidden (the
    browser would queue the transitions and dump them all on return), rotate at
    all under `prefers-reduced-motion`, or make itself keyboard-reachable — it is
    decoration, `alt=""`, and the copy beside it carries the message. */
export function HeroPhoto({
  frames,
  interval = 5600,
  className = '',
}: { frames: readonly string[]; interval?: number; className?: string }) {
  const [at, setAt] = useState(0);

  useEffect(() => {
    if (frames.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let timer = 0;
    const start = () => { if (!timer) timer = window.setInterval(() => setAt(i => (i + 1) % frames.length), interval); };
    const stop = () => { if (timer) { window.clearInterval(timer); timer = 0; } };
    const onVisibility = () => (document.hidden ? stop() : start());

    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [frames, interval]);

  return (
    <div className={`photo photo--cycle ${className}`.trim()}>
      {frames.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          className={i === at ? 'on' : ''}
          /* The first frame is the fold; the rest can wait for it. */
          loading={i === 0 ? 'eager' : 'lazy'}
          fetchPriority={i === 0 ? 'high' : 'low'}
          decoding="async"
        />
      ))}
    </div>
  );
}

/** The tick inside a feature-list bullet. The caller owns the surrounding chip. */
export const Ck = ({ size = 13 }: { size?: number }) => <Check size={size} strokeWidth={3.4} />;

/** Centred section header: kicker, heading, optional lead. */
export function SHead({ k, h2, lead, id }: { k: string; h2: ReactNode; lead?: ReactNode; id?: string }) {
  return (
    <div className="shead rv" id={id}>
      <div className="k">{k}</div>
      <h2>{h2}</h2>
      {lead && <p>{lead}</p>}
    </div>
  );
}

/* ── section photo band ───────────────────────────────────────────────────── */
/** A section-width photograph of whatever the section heading above it claims,
    with an optional line of copy over the dark side of the gradient. Sits under
    the SHead, ahead of the section's cards.

    `align` picks which end of the frame the copy covers — put it on the empty
    end, so the wash never lands on the thing the photo is there to show. */
export function PhotoBand({
  src, alt, title, lead, align = 'left',
}: { src: string; alt: string; title?: string; lead?: string; align?: 'left' | 'right' }) {
  return (
    <figure className={`pband rv${align === 'right' ? ' right' : ''}`}>
      <img src={src} alt={alt} loading="lazy" />
      {(title || lead) && (
        <figcaption>
          {title && <b>{title}</b>}
          {lead && <span>{lead}</span>}
        </figcaption>
      )}
    </figure>
  );
}

/* ── audience grid ────────────────────────────────────────────────────────── */
export type Audience = { icon: ReactNode; bg: string; fg: string; title: string; sub: string };

export function AudienceGrid({ items }: { items: Audience[] }) {
  return (
    <div className="aud">
      {items.map((a, i) => (
        <div className="acard rv" style={{ transitionDelay: `${(i % 4) * 60}ms` }} key={a.title}>
          <span className="ic" style={{ background: a.bg, color: a.fg }}>{a.icon}</span>
          <h3>{a.title}</h3>
          <p>{a.sub}</p>
        </div>
      ))}
    </div>
  );
}

/* Digital-professional audiences — the accounting & home pages.
   "OnlyFans" from the reference deck is deliberately not here: adult platforms are
   illegal to operate from the UAE, so naming one on a UAE compliance page would be
   advertising work we can't file. Patreon carries the same "paid audience" meaning. */
export const ONLINE_AUDIENCE: Audience[] = [
  { icon: <D_Broadcast size={30} />, bg: '#FDE7E4', fg: '#F0776B', title: 'Content creators', sub: 'YouTube · Twitch · TikTok · Patreon' },
  { icon: <D_Brackets size={30} />, bg: '#E7EEFB', fg: '#5B8DEF', title: 'Developers', sub: 'Freelance · US clients · SaaS' },
  { icon: <D_Spectrum size={30} />, bg: '#FCE4EF', fg: '#E85C9A', title: 'Designers', sub: 'Illustration · Branding · Etsy' },
  { icon: <D_Apex size={30} />, bg: '#FDF0DA', fg: '#F0A640', title: 'Online educators', sub: 'Hotmart · Udemy · own academies' },
  { icon: <D_Board size={30} />, bg: '#EEE7FA', fg: '#7A4FC0', title: 'Consultants', sub: 'Marketing · Coaching · B2B' },
  { icon: <D_Commerce size={30} />, bg: '#E4F3EA', fg: '#15824E', title: 'E-commerce', sub: 'Shopify · Amazon · Dropshipping' },
  { icon: <D_Lens size={30} />, bg: '#EAE7FB', fg: '#6C5CE7', title: 'Photo & video', sub: 'Stock · Weddings · Licensing' },
  { icon: <D_Stroke size={30} />, bg: '#E4F3EA', fg: '#15824E', title: 'Copywriters', sub: 'Freelance · Substack · Newsletters' },
];

/** Corporate Services — what the one-team desk actually covers. */
export const CORPORATE_AUDIENCE: Audience[] = [
  { icon: <D_Structure size={30} />, bg: '#FCE4D6', fg: '#D9662E', title: 'Mainland setup', sub: 'Dubai DED · trade licence' },
  { icon: <D_GlobeMesh size={30} />, bg: '#E7EEFB', fg: '#2E6BD6', title: 'Free zone setup', sub: 'Meydan · IFZA · DMCC · 40+ zones' },
  { icon: <D_Credential size={30} />, bg: '#E4F3EA', fg: '#15824E', title: 'Residence visas', sub: 'Founder · employee · family' },
  { icon: <D_Seal size={30} />, bg: '#FDF0DA', fg: '#F0A640', title: 'Golden Visa', sub: '10-year residence · investors & talent' },
  { icon: <D_Imprint size={30} />, bg: '#EEE7FA', fg: '#7A4FC0', title: 'PRO & renewals', sub: 'Amendments · NOCs · attestation' },
  { icon: <D_Purse size={30} />, bg: '#E1F4F1', fg: '#0E9E8E', title: 'WPS payroll', sub: 'Wage protection, run for you' },
  { icon: <D_Balance size={30} />, bg: '#FCE4EF', fg: '#E85C9A', title: 'Wills & POA', sub: 'DIFC wills · powers of attorney' },
  { icon: <D_Cycle size={30} />, bg: '#FCE4D6', fg: '#D9662E', title: 'Licence renewals', sub: 'Nothing lapses, a managed calendar' },
];

/* ── how we work: app mock beside numbered steps ──────────────────────────── */
export type Step = { h: string; p: string };

export function StepList({ steps }: { steps: Step[] }) {
  return (
    <div className="steps">
      {steps.map((s, i) => (
        <div className="step rv" style={{ transitionDelay: `${i * 80}ms` }} key={s.h}>
          <div className="n">{i + 1}</div>
          <div><h3>{s.h}</h3><p>{s.p}</p></div>
        </div>
      ))}
    </div>
  );
}
/* ── the dashboard mock ───────────────────────────────────────────────────────
   The signed-in product, drawn to the same plan as the real one: the sidebar
   groups of Sidebar.tsx, the page header and the white-cards-on-cream body of
   Dashboard.tsx. Only the figures are illustrative.

   `AppFrame` is the whole app in browser chrome (the "how we work" section);
   `DashPanel` is the same body without the sidebar (the hero peek). Both build
   from the same card pieces below, so the two mocks stay in step. */

const APP_LOGO = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/Conncct-logo.png';
const WELCOME = "Welcome back, here's what's happening today.";

export type NavGroup = { label?: string; items: { icon: AbIcon; label: string }[] };

/** The client sidebar, group for group, in its accounting view. */
export const APP_NAV: NavGroup[] = [
  {
    items: [
      { icon: AbGrid, label: 'Dashboard' },
      { icon: AbStructure, label: 'Company' },
      { icon: AbPerson, label: 'Profile' },
      { icon: AbStack, label: 'Data Room' },
      { icon: AbDoc, label: 'Articles' },
    ],
  },
  { label: 'Analytics', items: [{ icon: AbAscend, label: 'Financial Health' }] },
  {
    items: [
      { icon: AbCase, label: 'My Services' },
      { icon: AbJoin, label: 'Refer and Earn' },
      { icon: AbTuner, label: 'Settings' },
    ],
  },
  {
    label: 'Communications',
    items: [
      { icon: AbPing, label: 'Notifications' },
      { icon: AbDialogue, label: 'Message Us' },
    ],
  },
];

/** The header every signed-in page opens with. */
function DashHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="dhead">
      <div><b>{title}</b><span className="sub">{sub}</span></div>
    </div>
  );
}

/** The whole app — sidebar, page header, dashboard body — in browser chrome. */
export function AppFrame({
  nav = APP_NAV, active = 'Dashboard', title = 'Dashboard', sub = WELCOME, children,
}: {
  nav?: NavGroup[]; active?: string; title?: string; sub?: string; children: ReactNode;
}) {
  return (
    <div className="dash rv">
      <div className="bar">
        <i style={{ background: '#E15D5D' }} /><i style={{ background: '#F0B429' }} /><i style={{ background: '#3CC26E' }} />
      </div>
      <div className="dbody">
        <div className="dnav">
          <div className="dlogo"><img src={APP_LOGO} alt="" loading="lazy" /></div>
          {nav.map((g, gi) => (
            <div className="dgrp" key={g.label ?? gi}>
              {g.label && <h4>{g.label}</h4>}
              {g.items.map(({ icon: Icon, label }) => (
                <div className={`di${label === active ? ' on' : ''}`} key={label}>
                  <Icon size={13} strokeWidth={2.1} />{label}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="dmain">
          <DashHead title={title} sub={sub} />
          {children}
        </div>
      </div>
    </div>
  );
}

/** The dashboard body on its own, for the hero: same header, same cards, no sidebar. */
export function DashPanel({ title = 'Dashboard', sub = WELCOME, children }: {
  title?: string; sub?: string; children: ReactNode;
}) {
  return (
    <div className="hdash">
      <DashHead title={title} sub={sub} />
      {children}
    </div>
  );
}

/** The dashboard's two columns, wide then narrow, as the real grid splits them. */
export function DashGrid({ main, side }: { main: ReactNode; side: ReactNode }) {
  return <div className="dgrid"><div className="dcol">{main}</div><div className="dcol">{side}</div></div>;
}

/** A white dashboard card: uppercase eyebrow, heading, optional status pill. */
export function DashCard({ k, h, pill, dark, children }: {
  k?: string; h?: ReactNode; pill?: ReactNode; dark?: boolean; children?: ReactNode;
}) {
  return (
    <div className={`dcard${dark ? ' dark' : ''}`}>
      {(k || h || pill) && (
        <div className="ch">
          <div>{k && <span className="k">{k}</span>}{h && <h4>{h}</h4>}</div>
          {pill}
        </div>
      )}
      {children}
    </div>
  );
}

/** "Get started": the onboarding checklist, progress bar and all. */
export function DashStart({ title = 'Get started', lead = 'A few quick steps to unlock everything.', steps }: {
  title?: string; lead?: string; steps: { label: string; done?: boolean }[];
}) {
  const done = steps.filter(s => s.done).length;
  return (
    <div className="dcard dstart">
      <div className="ch">
        <div><h4>{title}</h4><span className="k2">{lead}</span></div>
        <b className="cnt">{done}/{steps.length} done</b>
      </div>
      <div className="dbar"><i style={{ width: `${Math.round((done / steps.length) * 100)}%` }} /></div>
      {steps.map((s, i) => (
        <div className={`dstep${s.done ? ' done' : ''}`} key={s.label}>
          <span className="b">{s.done ? <Check size={9} strokeWidth={4} /> : i + 1}</span>
          <span>{s.label}</span>
          {i === done && <em>Start →</em>}
        </div>
      ))}
    </div>
  );
}

/** The order tracker: submitted → in review → processing → complete. */
export function DashTrack({ steps, at }: { steps: string[]; at: number }) {
  return (
    <div className="dtrack">
      {steps.map((s, i) => (
        <div className={i < at ? 'done' : i === at ? 'now' : ''} key={s}>
          {i > 0 && <span className="ln" />}
          <span className="dt">{i < at ? <Check size={9} strokeWidth={4} /> : i + 1}</span>
          <span className="lb">{s}</span>
        </div>
      ))}
    </div>
  );
}

/** The setup-progress ring and its checklist, from the compliance card. */
export function DashRing({ pct, label, checks }: {
  pct: number; label: string; checks: { label: string; done?: boolean }[];
}) {
  const circ = 2 * Math.PI * 26;
  return (
    <div className="dringwrap">
      <div className="dring">
        <svg width="68" height="68" viewBox="0 0 68 68" aria-hidden="true">
          <circle cx="34" cy="34" r="26" fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="6" />
          <circle
            cx="34" cy="34" r="26" fill="none" stroke="var(--yellow)" strokeWidth="6" strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)}
          />
        </svg>
        <b>{pct}</b>
      </div>
      <span className="rl">{label}</span>
      <div className="dchks">
        {checks.map(c => (
          <div className={`dchk${c.done ? ' done' : ''}`} key={c.label}>
            <i>{c.done && <Check size={9} strokeWidth={4} />}</i>{c.label}
          </div>
        ))}
      </div>
    </div>
  );
}

/** The money tiles: income, expenses, profit. */
export function DashKpi({ items }: { items: { v: string; l: string; t: 'b' | 'p' | 'g' }[] }) {
  return (
    <div className="dkpi">
      {items.map(i => <div className={i.t} key={i.l}><b>{i.v}</b><span>{i.l}</span></div>)}
    </div>
  );
}

/* ── human touch band ─────────────────────────────────────────────────────── */
export type ChatLine = { from: 'them' | 'you'; text: string };

export function HumanBand({
  title, lead, feats, adviser, role, avatar, chat, photo,
}: {
  title: ReactNode; lead: string; feats: string[];
  adviser: string; role: string; avatar: ReactNode; chat: ChatLine[];
  /** Optional photograph of the work the heading promises. The caption
      describes the work, never the named adviser beside it: the chat card is
      Conncct's own person, the stock photograph is not. */
  photo?: { src: string; alt: string; caption?: string };
}) {
  return (
    <div className="human rv">
      <div>
        <span className="eyebrow soft">The human touch</span>
        <h2>{title}</h2>
        <p>{lead}</p>
        <div className="feats">{feats.map(f => <span key={f}>{f}</span>)}</div>
        {photo && (
          <figure className="hphoto">
            <img src={photo.src} alt={photo.alt} loading="lazy" />
            {photo.caption && <figcaption>{photo.caption}</figcaption>}
          </figure>
        )}
      </div>
      <div className="adv">
        <div className="advhead">
          <span className="av">{avatar}</span>
          <div><b>{adviser} · {role}</b><span className="dotline" style={{ color: '#3CD68A' }}>Online now</span></div>
        </div>
        <div className="chat">
          {chat.map((c, i) => <div className={`bub ${c.from}`} key={i}>{c.text}</div>)}
        </div>
      </div>
    </div>
  );
}

/* ── proof: stat row + one client story ───────────────────────────────────── */
export function Stats({ items }: { items: { b: string; s: ReactNode }[] }) {
  return (
    <div className="stats">
      {items.map((s, i) => (
        <div className="stat rv" style={{ transitionDelay: `${i * 60}ms` }} key={s.b + i}>
          <b>{s.b}</b><span>{s.s}</span>
        </div>
      ))}
    </div>
  );
}

export function ClientStory({
  quote, name, note, initial, avatar, photo, caption,
}: {
  quote: string; name: string; note: string; initial: string;
  /** Face crop for the byline chip. Without one the chip falls back to `initial`. */
  avatar?: string;
  photo: string; caption: string;
}) {
  return (
    <div className="testi rv">
      <div className="tbody">
        <div className="k">Client story</div>
        <div className="q">“{quote}”</div>
        <div className="who">
          <div className="pl">
            {avatar ? <img src={avatar} alt="" loading="lazy" /> : initial}
          </div>
          <div>
            <b>{name}</b>
            <div className="wnote">{note}</div>
          </div>
        </div>
      </div>
      {/* The client, not a scene: a photograph of the person who said the words
          is the only version of this section that reads as evidence. And it stays
          a still — there is no client film, and a play button with nothing behind
          it is worse than none. */}
      <figure className="shot">
        <img src={photo} alt="" loading="lazy" />
        <figcaption>{caption}</figcaption>
      </figure>
    </div>
  );
}

/* ── comparison table ─────────────────────────────────────────────────────── */
export type CmpRow = [string, string, string, string];

export function Compare({ cols, rows }: { cols: [string, string, string]; rows: CmpRow[] }) {
  const cell = (v: string) =>
    v === 'yes' ? <Check size={17} strokeWidth={3} className="y" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
    : v === 'no' ? <X size={17} strokeWidth={3} className="n" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
    : <span className="p">{v}</span>;
  return (
    <div className="cmpwrap rv">
      <div className="cmpscroll">
        <table className="compare">
          <thead>
            <tr>
              <th className="cap" aria-label="Capability" />
              <th className="us">{cols[0]}</th><th>{cols[1]}</th><th>{cols[2]}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([cap, a, b, c]) => (
              <tr key={cap}>
                <td className="cap">{cap}</td>
                <td className="usc">{cell(a)}</td>
                <td>{cell(b)}</td>
                <td>{cell(c)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── FAQ (accordion, one open at a time) ──────────────────────────────────── */
export function Faq({ items }: { items: { q: string; a: ReactNode }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="faq">
      {items.map((f, i) => (
        <div className={`qa rv${open === i ? ' open' : ''}`} key={f.q}>
          <button type="button" aria-expanded={open === i} onClick={() => setOpen(open === i ? null : i)}>
            {f.q}<Plus className="pm" size={20} strokeWidth={2.2} aria-hidden />
          </button>
          <div className="a"><p>{f.a}</p></div>
        </div>
      ))}
    </div>
  );
}

/* ── guarantee strip ──────────────────────────────────────────────────────── */
export function Guarantee({ title, body }: { title: string; body: string }) {
  return (
    <div className="guarantee rv">
      <span className="g-ic"><AbAegis size={24} strokeWidth={1.9} /></span>
      <div><b>{title}</b><p>{body}</p></div>
    </div>
  );
}

/* ── final CTA ────────────────────────────────────────────────────────────── */
export function CtaBand({ title, body, action }: { title: string; body: string; action: ReactNode }) {
  return (
    <section className="sec">
      <div className="wrap">
        <div className="cta rv">
          <h2>{title}</h2>
          <p>{body}</p>
          {action}
        </div>
      </div>
    </section>
  );
}
